import React, { useState } from 'react';
import type { Venue } from '../../../types/venue';
import { updateVenue } from '../../../utils/venueApi';
import Button from '../../ui/Button';

interface WifiSettingsPanelProps {
  venue: Venue;
  onUpdate: (venue: Venue) => void;
}

const inputBase =
  'w-full px-3 py-2.5 border border-coffee-200 rounded-lg text-sm text-coffee-900 bg-white focus:outline-none focus:ring-2 focus:ring-coffee-500 focus:border-coffee-400 transition-colors';

const WifiSettingsPanel: React.FC<WifiSettingsPanelProps> = ({ venue, onUpdate }) => {
  const [ssid, setSsid] = useState(venue.wifi?.ssid ?? '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rotateReminder, setRotateReminder] = useState(venue.wifi?.rotateReminder ?? false);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const handleSave = async () => {
    setSaving(true);
    setFeedback(null);
    try {
      const wifiData: any = { ssid, rotateReminder };
      if (password) wifiData.password = password;
      const updated = await updateVenue(venue.id, { wifi: wifiData } as any);
      onUpdate(updated);
      setPassword('');
      setFeedback({ type: 'success', message: 'Wi-Fi settings saved.' });
    } catch (err: any) {
      setFeedback({ type: 'error', message: err.message || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow mb-1.5">Wi-Fi</div>
        <h2 className="text-display text-2xl text-coffee-900">Wi-Fi settings</h2>
      </div>

      <div className="bg-white border border-coffee-100 rounded-2xl p-6 space-y-4">
        <div>
          <label htmlFor="wifi-ssid-setting" className="block text-sm font-medium text-coffee-800 mb-1.5">
            Network name (SSID)
          </label>
          <input
            id="wifi-ssid-setting"
            type="text"
            value={ssid}
            onChange={(e) => setSsid(e.target.value)}
            className={inputBase}
          />
        </div>

        <div>
          <label htmlFor="wifi-password-setting" className="block text-sm font-medium text-coffee-800 mb-1.5">
            New password{' '}
            <span className="text-coffee-400 text-xs font-normal">(leave blank to keep current)</span>
          </label>
          <div className="relative">
            <input
              id="wifi-password-setting"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              className={`${inputBase} pr-16`}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-coffee-500 hover:text-coffee-800 font-medium transition-colors"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? 'Hide' : 'Show'}
            </button>
          </div>
        </div>

        <label className="flex items-center gap-3 p-4 rounded-xl border border-coffee-100 bg-coffee-50 cursor-pointer">
          <input
            type="checkbox"
            checked={rotateReminder}
            onChange={(e) => setRotateReminder(e.target.checked)}
            className="h-4 w-4 rounded border-coffee-300 text-coffee-600 focus:ring-coffee-500"
          />
          <span className="text-sm text-coffee-800">Monthly password rotation reminder</span>
        </label>
      </div>

      {feedback && (
        <div className={`text-sm rounded-xl p-4 border ${
          feedback.type === 'success'
            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
            : 'bg-red-50 text-red-700 border-red-200'
        }`}>
          {feedback.message}
        </div>
      )}

      <Button variant="primary" onClick={handleSave} loading={saving} disabled={saving || !ssid.trim()}>
        {saving ? 'Saving...' : 'Save settings'}
      </Button>
    </div>
  );
};

export default WifiSettingsPanel;
